import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@/context/ThemeContext';
import { Toaster } from '@/components/ui/sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import ScrollToTop from '@/components/ScrollToTop';

import LandingPage from '@/pages/LandingPage';
import PublicTools from '@/pages/PublicTools';
import FeaturesPage from '@/pages/FeaturesPage';
import IntegrationsPage from '@/pages/IntegrationsPage';
import ForRestaurantsPage from '@/pages/ForRestaurantsPage';
import ForCafesBakeriesPage from '@/pages/ForCafesBakeriesPage';
import ForGrocerySpecialtyPage from '@/pages/ForGrocerySpecialtyPage';
import ForGhostKitchensPopupsPage from '@/pages/ForGhostKitchensPopupsPage';

import '@/App.css';

/**
 * merchants.hungrycanadian.com — public merchant-facing landing site.
 *
 * The previous merchant/admin portal has been removed. The only active
 * surfaces are:
 *   /         → LandingPage (merchant discovery + lead capture)
 *   /tools    → PublicTools (public marketing calculators)
 *
 * All legacy portal/auth/admin/dashboard/onboarding routes either redirect
 * to / (for inbound links / SEO) or return a 404 ( /register only ).
 */

const NotFound = () => (
  <div className="min-h-screen flex items-center justify-center bg-background px-6 text-center">
    <div className="max-w-md">
      <p className="text-sm font-semibold uppercase tracking-wide text-primary mb-3">404</p>
      <h1 className="text-3xl font-heading font-bold text-foreground mb-3">Page not found</h1>
      <p className="text-muted-foreground mb-8">
        The page you were looking for is no longer available.
      </p>
      <a
        href="/"
        data-testid="not-found-home-link"
        className="inline-flex items-center h-11 px-6 rounded-lg bg-primary hover:bg-primary-hover text-primary-foreground text-sm font-semibold transition-colors"
      >
        Go to homepage
      </a>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/tools" element={<PublicTools />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/for-restaurants" element={<ForRestaurantsPage />} />
            <Route path="/for-cafes-bakeries" element={<ForCafesBakeriesPage />} />
            <Route path="/for-grocery-specialty" element={<ForGrocerySpecialtyPage />} />
            <Route path="/for-ghost-kitchens-popups" element={<ForGhostKitchensPopupsPage />} />

            {/* /join was the legacy landing path — redirect to / */}
            <Route path="/join" element={<Navigate to="/" replace />} />

            {/* /register no longer exists — explicit 404 per product decision */}
            <Route path="/register" element={<NotFound />} />

            {/* All legacy portal/auth/admin/dashboard paths → / */}
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/forgot-password" element={<Navigate to="/" replace />} />
            <Route path="/reset-password" element={<Navigate to="/" replace />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/onboarding" element={<Navigate to="/" replace />} />
            <Route path="/messages" element={<Navigate to="/" replace />} />
            <Route path="/resources/*" element={<Navigate to="/" replace />} />
            <Route path="/settings" element={<Navigate to="/" replace />} />
            <Route path="/pending" element={<Navigate to="/" replace />} />
            <Route path="/inactive" element={<Navigate to="/" replace />} />
            <Route path="/survey" element={<Navigate to="/" replace />} />
            <Route path="/admin/*" element={<Navigate to="/" replace />} />

            {/* Catch-all — anything else is a 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
